import { ref, onMounted, onUnmounted, watch } from 'vue';
import { Web } from 'sip.js';
import { useSipPassword } from './useSipPassword';
import { emitter } from 'shared/helpers/mitt';

export function useSipAudio(options = {}) {
  const { onCallHangup, sipExtension: sipExtensionRef } = options;

  const { getSipPassword, refreshSipPassword } = useSipPassword({
    cacheTTL: 5 * 60 * 1000,
  });

  const sipStatus = ref('unregistered');
  const error = ref(null);
  const isInCall = ref(false);

  const WSS_URL =
    import.meta.env.VITE_ASTERISK_WSS_URL ||
    'wss://asterisk-sip.lhgmoteis.com.br';
  const DEFAULT_SIP_EXTENSION = import.meta.env.VITE_SIP_EXTENSION || '1000';
  const SIP_DOMAIN = import.meta.env.VITE_SIP_DOMAIN || '167.172.222.23';

  let simpleUser = null;
  let currentExtension = null; // ramal CONFIRMADO (registro bem-sucedido)
  let intendedExtension = null; // ramal PRETENDIDO (o que estamos tentando registrar)

  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let watchdogTimer = null;
  let isIntentionalDisconnect = false;

  let activePeerConnection = null;
  let qualityCheckInterval = null;
  let localAudioContext = null;
  let keepAliveAudioCtx = null;

  const extractExtensionNumber = sipExtension => {
    if (!sipExtension) return DEFAULT_SIP_EXTENSION;
    return sipExtension.replace(/^SIP\//, '');
  };

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = sipExtension => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    // Sem limite de tentativas de propósito: um ramal que desiste deixa o agente
    // com o widget vivo e o telefone mudo, sem perceber, até alguém reclamar.
    // Melhor tentar a cada 30s para sempre; o teto baixo (era 128s) também
    // encurta o tempo cego depois de uma queda de rede.
    const delay = Math.min(2000 * 2 ** reconnectAttempts, 30000);
    reconnectAttempts += 1;

    reconnectTimer = setTimeout(async () => {
      await initSip(sipExtension);
    }, delay);
  };

  // Vigia: a cada 30s confere se o ramal continua registrado e re-inicia se não.
  // Rede de segurança para qualquer modo de falha não previsto — um callback do
  // SIP.js que não dispara, transporte morto sem evento, aba retomada de suspensão.
  // Sem isto o agente só descobre que está mudo ao perder uma ligação.
  const startWatchdog = () => {
    if (watchdogTimer) return;
    watchdogTimer = setInterval(() => {
      if (isIntentionalDisconnect || isInCall.value) return;
      if (sipStatus.value === 'registered') return;
      if (!intendedExtension || reconnectTimer) return;
      reconnectAttempts = 0;
      initSip(intendedExtension);
    }, 30000);
  };

  const stopWatchdog = () => {
    if (watchdogTimer) {
      clearInterval(watchdogTimer);
      watchdogTimer = null;
    }
  };

  const startAudioEnhancement = async pc => {
    const audioSender = pc.getSenders().find(s => s.track?.kind === 'audio');
    if (!audioSender?.track) return;

    // Guarda o microfone real para poder desfazer a troca. Se o AudioContext ficar
    // 'suspended' (autoplay policy do Chrome em aba de fundo), a trilha processada
    // sai em SILÊNCIO ABSOLUTO e o replaceTrack deixaria o agente mudo SEM lançar
    // erro (o catch não pega). Áudio original >> áudio mudo.
    const originalTrack = audioSender.track;

    try {
      localAudioContext = new AudioContext();

      if (localAudioContext.state === 'suspended') {
        await localAudioContext.resume();
      }

      // Só troca se o contexto está REALMENTE processando. Se não saiu de
      // suspended, aborta e mantém o microfone original.
      if (localAudioContext.state !== 'running') {
        console.warn('[SipAudio] AudioContext não saiu de suspended — mantendo microfone original (sem enhancement)');
        localAudioContext.close().catch(() => {});
        localAudioContext = null;
        return;
      }

      const source = localAudioContext.createMediaStreamSource(
        new MediaStream([originalTrack])
      );

      const highpass = localAudioContext.createBiquadFilter();
      highpass.type = 'highpass';
      highpass.frequency.value = 80;
      highpass.Q.value = 0.7;

      const compressor = localAudioContext.createDynamicsCompressor();
      compressor.threshold.value = -24;
      compressor.knee.value = 12;
      compressor.ratio.value = 3;
      compressor.attack.value = 0.003;
      compressor.release.value = 0.25;

      const gain = localAudioContext.createGain();
      gain.gain.value = 2.0;

      const destination = localAudioContext.createMediaStreamDestination();

      source.connect(highpass);
      highpass.connect(compressor);
      compressor.connect(gain);
      gain.connect(destination);

      const enhancedTrack = destination.stream.getAudioTracks()[0];
      await audioSender.replaceTrack(enhancedTrack);

      // Rede de segurança: se o contexto cair de 'running' durante a chamada (aba
      // vai para segundo plano no meio), volta ao microfone original para não
      // emudecer o agente.
      localAudioContext.onstatechange = () => {
        if (localAudioContext && localAudioContext.state !== 'running') {
          audioSender.replaceTrack(originalTrack).catch(() => {});
        }
      };

      console.log('[SipAudio] 🎙️ Pipeline de melhoria de áudio ativo');
    } catch (e) {
      // Em qualquer erro, garante o microfone original no ar (nunca mudo).
      audioSender.replaceTrack(originalTrack).catch(() => {});
      console.warn('[SipAudio] Pipeline de áudio não iniciado, microfone original mantido:', e.message);
    }
  };

  const stopAudioEnhancement = () => {
    if (localAudioContext) {
      localAudioContext.onstatechange = null; // não reverter o track no teardown intencional
      localAudioContext.close().catch(() => {});
      localAudioContext = null;
    }
  };

  // Keep-alive de áudio: mantém um AudioContext ativo com um oscilador de ganho
  // ínfimo (0.0001 — inaudível, mas gera amostras não-zero, então o Chrome
  // classifica a aba como "playing audio" e reduz/isenta o throttling de timers
  // em background). Isso ajuda o sip.js a responder o INVITE a tempo mesmo com a
  // aba em segundo plano. Não é garantia — o timeout de answerCall é a rede de
  // segurança. Precisa de resume() por causa da autoplay policy (senão vira no-op).
  const startKeepAliveAudio = async () => {
    if (keepAliveAudioCtx) return;
    try {
      keepAliveAudioCtx = new AudioContext();
      const osc = keepAliveAudioCtx.createOscillator();
      const gain = keepAliveAudioCtx.createGain();
      gain.gain.value = 0.0001;
      osc.connect(gain);
      gain.connect(keepAliveAudioCtx.destination);
      osc.start();
      if (keepAliveAudioCtx.state === 'suspended') {
        await keepAliveAudioCtx.resume();
      }
    } catch (e) {
      // Não crítico: se falhar, a chamada segue e o timeout de answerCall protege.
    }
  };

  const stopKeepAliveAudio = () => {
    if (keepAliveAudioCtx) {
      keepAliveAudioCtx.close().catch(() => {});
      keepAliveAudioCtx = null;
    }
  };

  const startIceMonitoring = pc => {
    if (!pc) return;
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      if ((state === 'disconnected' || state === 'failed') && isInCall.value) {
        try {
          simpleUser?.session?.renegotiate({
            sessionDescriptionHandlerOptions: {
              offerOptions: { iceRestart: true },
            },
          });
        } catch (e) {
          // renegotiate pode não estar disponível nesta versão do SIP.js — ignora
        }
      }
    };
  };

  const startQualityMonitoring = pc => {
    if (!pc) return;
    let lastLost = 0;
    let lastReceived = 0;

    qualityCheckInterval = setInterval(async () => {
      if (!pc || !isInCall.value) return;
      try {
        const stats = await pc.getStats();
        stats.forEach(report => {
          if (report.type === 'inbound-rtp' && report.kind === 'audio') {
            const deltaLost = report.packetsLost - lastLost;
            const deltaReceived = report.packetsReceived - lastReceived;
            const total = deltaLost + deltaReceived;
            lastLost = report.packetsLost;
            lastReceived = report.packetsReceived;

            emitter.emit('callQualityUpdate', {
              fractionLost: total > 0 ? deltaLost / total : 0,
              jitter: report.jitter || 0,
            });
          }
        });
      } catch (e) {
        // ignora erros de stats
      }
    }, 3000);
  };

  const stopQualityMonitoring = () => {
    if (qualityCheckInterval) {
      clearInterval(qualityCheckInterval);
      qualityCheckInterval = null;
    }
    if (activePeerConnection) {
      activePeerConnection.oniceconnectionstatechange = null;
      activePeerConnection = null;
    }
  };

  const initSip = async (sipExtension = null) => {
    const extensionToUse = extractExtensionNumber(sipExtension);
    intendedExtension = extensionToUse; // marca o pretendido mesmo antes de confirmar

    // Zera o delegate ANTES do disconnect para que o onServerDisconnect do
    // simpleUser antigo não dispare scheduleReconnect enquanto o novo initSip já
    // roda — evita que reconnectAttempts seja incrementado múltiplas vezes por
    // oscilação de rede (delegates órfãos estourariam o máximo em ~5 oscilações).
    if (simpleUser) {
      simpleUser.delegate = {};
      simpleUser.disconnect().catch(() => {});
      simpleUser = null;
    }

    try {
      const sipPassword = await getSipPassword(extensionToUse);

      const remoteAudio = new Audio();
      remoteAudio.autoplay = true;

      const sipOptions = {
        aor: `sip:${extensionToUse}@${SIP_DOMAIN}`,
        media: {
          constraints: {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
            video: false,
          },
          remote: {
            audio: remoteAudio,
          },
        },
        userAgentOptions: {
          authorizationUsername: extensionToUse,
          authorizationPassword: sipPassword,
          transportOptions: {
            server: WSS_URL,
          },
          sessionDescriptionHandlerFactoryOptions: {
            iceGatheringTimeout: 3000,
            peerConnectionConfiguration: {
              iceServers: [
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' },
                { urls: 'stun:stun2.l.google.com:19302' },
              ],
              sdpSemantics: 'unified-plan',
              rtcpMuxPolicy: 'negotiate',
            },
          },
        },
      };

      simpleUser = new Web.SimpleUser(WSS_URL, sipOptions);

      simpleUser.delegate = {
        onServerConnect: () => {
          reconnectAttempts = 0;
          sipStatus.value = 'registering';
          error.value = null;

          simpleUser
            .register()
            .then(() => {
              currentExtension = extensionToUse;
              sipStatus.value = 'registered';
              startKeepAliveAudio();
            })
            .catch(() => {
              error.value = 'Falha ao registrar no SIP';
              sipStatus.value = 'unregistered';
              scheduleReconnect(extensionToUse);
            });
        },
        onServerDisconnect: () => {
          sipStatus.value = 'unregistered';

          if (isIntentionalDisconnect) {
            isIntentionalDisconnect = false;
            return;
          }

          scheduleReconnect(extensionToUse);
        },
        onRegistered: () => {
          sipStatus.value = 'registered';
          startKeepAliveAudio();
        },
        onUnregistered: () => {
          sipStatus.value = 'unregistered';
        },
        onRegistrationFailed: cause => {
          if (
            cause?.includes('403') ||
            cause?.includes('Forbidden') ||
            cause?.includes('401')
          ) {
            refreshSipPassword(extensionToUse)
              .then(() => {
                emitter.emit('newToastMessage', {
                  message: 'Senha SIP atualizada. Reconectando...',
                });
                setTimeout(async () => {
                  await initSip(extensionToUse);
                }, 2000);
              })
              .catch(() => {
                error.value = 'Não foi possível atualizar a senha SIP';
              });
          } else {
            error.value = `Falha no registro: ${cause}`;
            scheduleReconnect(extensionToUse);
          }
        },
        onCallReceived: async () => {
          let answered = false;
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              await simpleUser.answer();
              answered = true;
              break;
            } catch (e) {
              console.warn(
                `[SipAudio] answer() falhou (tentativa ${attempt + 1}/3):`,
                e?.message
              );
              if (attempt < 2) {
                await new Promise(r => setTimeout(r, 800));
              }
            }
          }
          if (!answered) {
            isInCall.value = false;
          }
        },
        onCallAnswered: () => {
          if (!isInCall.value) {
            isInCall.value = true;
          }

          const pc =
            simpleUser?.session?.sessionDescriptionHandler?.peerConnection;
          if (pc) {
            activePeerConnection = pc;
            startIceMonitoring(pc);
            startQualityMonitoring(pc);
            startAudioEnhancement(pc);
          }
        },
        onCallHangup: () => {
          isInCall.value = false;
          stopQualityMonitoring();
          stopAudioEnhancement();

          if (onCallHangup && typeof onCallHangup === 'function') {
            onCallHangup();
          }
        },
      };

      simpleUser.connect().catch(err => {
        sipStatus.value = 'unregistered';
        error.value = err.message;
        // Correção principal: sem isto a cadeia de reconexão morre aqui — o
        // transporte não subiu, então onServerDisconnect nunca dispara e ninguém
        // reagenda (foi o caso da Julie: WebSocket caiu e o navegador parou de tentar).
        scheduleReconnect(extensionToUse);
      });
    } catch (err) {
      sipStatus.value = 'unregistered';
      error.value = err.message;
      scheduleReconnect(extensionToUse);
    }
  };

  const cleanup = (intentional = false) => {
    if (intentional) {
      isIntentionalDisconnect = true;
      reconnectAttempts = 0;
    }

    clearReconnectTimer();
    stopWatchdog();
    stopQualityMonitoring();
    stopAudioEnhancement();
    stopKeepAliveAudio();

    if (simpleUser) {
      simpleUser.disconnect().catch(() => {});
      simpleUser = null;
    }
    sipStatus.value = 'unregistered';
    isInCall.value = false;
  };

  const hangup = async () => {
    if (!simpleUser) {
      return;
    }

    try {
      if (!simpleUser.session) {
        isInCall.value = false;
        return;
      }

      await simpleUser.hangup();
      isInCall.value = false;
    } catch (err) {
      const errorMessage = err?.message || err?.toString() || '';

      if (
        !errorMessage.includes('Session does not exist') &&
        !errorMessage.includes('No session') &&
        !errorMessage.includes('session state')
      ) {
        // erro inesperado, ignorado intencionalmente
      }
      isInCall.value = false;
    }
  };

  const hasActiveCall = () => {
    return isInCall.value;
  };

  const reregister = async sipExtension => {
    const newExtension = extractExtensionNumber(sipExtension);

    // Compara com o ramal PRETENDIDO, não o confirmado: se o registro anterior
    // falhou, currentExtension fica nulo e a troca para o mesmo valor não seria
    // detectada.
    if (intendedExtension === newExtension) {
      return;
    }

    cleanup(true);
    await initSip(sipExtension);
  };

  const onNetworkOnline = async () => {
    if (!isIntentionalDisconnect && sipStatus.value !== 'registered' && intendedExtension) {
      reconnectAttempts = 0;
      clearReconnectTimer();
      await initSip(intendedExtension);
    }
  };

  const onNetworkOffline = () => {
    clearReconnectTimer();
  };

  const onVisibilityChange = async () => {
    if (!document.hidden && !isIntentionalDisconnect && sipStatus.value !== 'registered' && intendedExtension) {
      reconnectAttempts = 0;
      clearReconnectTimer();
      await initSip(intendedExtension);
    }
  };

  onMounted(async () => {
    const initialExtension = sipExtensionRef?.value || null;
    // Só inicia com ramal real. Sem isto, o componente monta antes do perfil
    // carregar e registra no DEFAULT (1000) — visto em produção: um contato saiu
    // do AOR 1000 e entrou no 1030 21s depois. O watch cobre quando o ramal chega;
    // o watchdog é a rede de segurança contínua.
    if (initialExtension) {
      await initSip(initialExtension);
    }
    startWatchdog();
    window.addEventListener('online', onNetworkOnline);
    window.addEventListener('offline', onNetworkOffline);
    document.addEventListener('visibilitychange', onVisibilityChange);
  });

  if (sipExtensionRef) {
    watch(sipExtensionRef, (newExtension, oldExtension) => {
      if (newExtension && newExtension !== oldExtension) {
        reregister(newExtension);
      }
    });
  }

  onUnmounted(() => {
    cleanup(true);
    window.removeEventListener('online', onNetworkOnline);
    window.removeEventListener('offline', onNetworkOffline);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  return {
    sipStatus,
    error,
    isInCall,
    hasActiveCall,
    hangup,
    cleanup,
    reregister,
  };
}
